// Terrain, drawn the way a printed map is drawn. Geometry comes from real coastline,
// river, and lake data (tools/buildmap.ts); nothing here is hand-placed. There is no
// border: the sea, the river, and the places people live are what identify a map.
//
// Paint order matters. The coastal rim is stroked on both sides of the shore *before*
// land is filled, so land covers its inner half and only the water side remains.
import { Container, Graphics, Text, type TextStyleOptions } from 'pixi.js';
import type { MapData, Point } from '../sim';
import { RIVER_CLEARANCE } from '../sim/geometry';
import { palette } from './palette';

export interface MapLayer {
  root: Container;
  /** Called when the camera scale changes so labels keep a constant screen size. */
  setScale(scale: number): void;
  /** Reveal a town's name once it has homes. */
  setTownVisible(name: string, visible: boolean): void;
}

const LABEL_SIZE = 11;

export function createMapLayer(map: MapData): MapLayer {
  const root = new Container();
  root.eventMode = 'none';

  const sea = new Graphics();
  drawSea(sea, map);
  root.addChild(sea);

  const marks = new Graphics();
  drawZoneMarks(marks, map);
  root.addChild(marks);

  const rivers = new Graphics();
  drawRivers(rivers, map);
  root.addChild(rivers);

  const lakes = new Graphics();
  drawLakes(lakes, map);
  root.addChild(lakes);

  const labels = new Container();
  const labelViews = new Map<string, Text>();
  for (const town of map.towns) {
    const text = new Text({ text: town.name.toUpperCase(), style: labelStyle() });
    text.anchor.set(0.5, 1);
    text.position.set(town.x, town.y - 40);
    text.alpha = 0;
    labels.addChild(text);
    labelViews.set(town.name, text);
  }
  root.addChild(labels);

  return {
    root,
    setScale(scale) {
      // Labels hold a near-constant screen size, but never so large that a city name
      // dominates a phone-sized board.
      const target = 9.5;
      const factor = scale > 0 ? target / (LABEL_SIZE * scale) : 1;
      const clamped = Math.max(0.75, Math.min(2.1, factor));
      for (const text of labelViews.values()) text.scale.set(clamped);
    },
    setTownVisible(name, visible) {
      const text = labelViews.get(name);
      if (!text) return;
      const target = visible ? 0.75 : 0;
      if (Math.abs(text.alpha - target) > 0.01) text.alpha = target;
    },
  };
}

function labelStyle(): TextStyleOptions {
  return {
    fontFamily: '-apple-system, BlinkMacSystemFont, Inter, Segoe UI, Helvetica, Arial, sans-serif',
    fontSize: LABEL_SIZE,
    fontWeight: '600',
    letterSpacing: 1.2,
    fill: palette.label,
  };
}

/** Sea everywhere the data covers, then a rim along every shore, then land on top. */
function drawSea(g: Graphics, map: MapData): void {
  const margin = map.projection?.margin ?? 0.7;
  const mx = map.size.w * margin;
  const my = map.size.h * margin;
  g.rect(-mx, -my, map.size.w + 2 * mx, map.size.h + 2 * my).fill({ color: palette.water });

  for (const ring of map.land) {
    trace(g, ring);
    g.stroke({ color: palette.waterRim, width: 11, join: 'round', alpha: 0.9 });
  }
  for (const ring of map.land) {
    trace(g, ring);
    g.fill({ color: palette.background });
  }
  for (const ring of map.land) {
    trace(g, ring);
    g.stroke({ color: palette.shoreline, width: 1.1, join: 'round' });
  }
}

/** A river is water: a ribbon with banks that widens toward the sea. */
function drawRivers(g: Graphics, map: MapData): void {
  for (const river of map.rivers) {
    const pts = river.points;
    // Width follows arc length so tributary-free stretches still taper evenly.
    const cumulative: number[] = [0];
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1]!;
      const b = pts[i]!;
      cumulative.push(cumulative[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1]));
    }
    const total = cumulative[cumulative.length - 1] || 1;
    const widthAt = (i: number): number =>
      RIVER_CLEARANCE * 2 * (0.42 + 0.58 * (cumulative[i]! / total));
    for (const bank of [true, false]) {
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i]!;
        const b = pts[i + 1]!;
        g.moveTo(a[0], a[1]).lineTo(b[0], b[1]);
        g.stroke({
          color: bank ? palette.shoreline : palette.water,
          width: widthAt(i) * 0.5 + (bank ? 2 : 0),
          cap: 'round',
          join: 'round',
        });
      }
    }
  }
}

function drawLakes(g: Graphics, map: MapData): void {
  for (const lake of map.lakes) {
    trace(g, lake.polygon);
    g.fill({ color: palette.water });
    trace(g, lake.polygon);
    g.stroke({ color: palette.waterRim, width: 5, alignment: 1, join: 'round', alpha: 0.9 });
    trace(g, lake.polygon);
    g.stroke({ color: palette.shoreline, width: 1.1, join: 'round' });
  }
}

/**
 * Terrain hints are drawn the way a paper map draws them: a field of small repeated
 * marks that thins out toward the edge of the region. A field has no boundary, so it
 * can never be mistaken for something the player must stay inside.
 */
function drawZoneMarks(g: Graphics, map: MapData): void {
  for (const zone of map.zones) {
    const area = polygonArea(zone.polygon);
    const fade = Math.min(48, Math.sqrt(area) / 3);
    for (const [x, y, jitter] of scatter(zone.polygon, 27)) {
      const edge = distanceToEdge(x, y, zone.polygon);
      const alpha = Math.min(1, edge / fade) * (zone.type === 'sun' ? 0.55 : 0.62);
      if (alpha < 0.05) continue;
      if (zone.type === 'sun') {
        const r = 2.6 + jitter * 1.2;
        for (let i = 0; i < 3; i++) {
          const a = (i * Math.PI) / 3;
          g.moveTo(x - Math.cos(a) * r, y - Math.sin(a) * r);
          g.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
        }
        g.stroke({ color: palette.zoneSun, width: 1, alpha, cap: 'round' });
      } else {
        const w = 5 + jitter * 3;
        g.moveTo(x - w, y);
        g.bezierCurveTo(x - w * 0.4, y - 2.2, x + w * 0.4, y + 2.2, x + w, y);
        g.stroke({ color: palette.zoneWind, width: 1, alpha, cap: 'round' });
      }
    }
  }
}

function polygonArea(polygon: Point[]): number {
  let sum = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i]!;
    const pj = polygon[j]!;
    sum += pj[0] * pi[1] - pi[0] * pj[1];
  }
  return Math.abs(sum / 2);
}

function distanceToEdge(x: number, y: number, polygon: Point[]): number {
  let best = Infinity;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j]!;
    const b = polygon[i]!;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lenSq = dx * dx + dy * dy;
    const t =
      lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / lenSq));
    const px = a[0] + t * dx - x;
    const py = a[1] + t * dy - y;
    best = Math.min(best, Math.sqrt(px * px + py * py));
  }
  return best;
}

/** Evenly but not regularly spread points: a grid whose cells are nudged by a hash. */
function scatter(polygon: Point[], spacing: number): [number, number, number][] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of polygon) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  const out: [number, number, number][] = [];
  const cols = Math.ceil((maxX - minX) / spacing);
  const rows = Math.ceil((maxY - minY) / spacing);
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const a = noise(i, j);
      const b = noise(i + 71, j + 13);
      const c = noise(i + 7, j + 29);
      const x = minX + (i + 0.5 + (a - 0.5) * 0.85) * spacing;
      const y = minY + (j + 0.5 + (b - 0.5) * 0.85) * spacing;
      if (inside(x, y, polygon)) out.push([x, y, c]);
    }
  }
  return out;
}

function noise(i: number, j: number): number {
  let h = Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

function inside(x: number, y: number, polygon: Point[]): boolean {
  let hit = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i]!;
    const pj = polygon[j]!;
    if (pi[1] > y !== pj[1] > y && x < ((pj[0] - pi[0]) * (y - pi[1])) / (pj[1] - pi[1]) + pi[0]) {
      hit = !hit;
    }
  }
  return hit;
}

function trace(g: Graphics, points: Point[]): void {
  const [first, ...rest] = points;
  if (!first) return;
  g.moveTo(first[0], first[1]);
  for (const p of rest) g.lineTo(p[0], p[1]);
  g.closePath();
}
